/*
 * Sketch to Slide — state machine.
 *
 * Tracks the live PowerPoint selection (mirrors usePhotoHeadshot.ts's
 * DocumentSelectionChanged wiring) so the panel can enable its one action
 * only once a single sketch image is selected. Running it walks: read
 * selection -> capture+upload the sketch -> analyze it (DeckorateAPI vision
 * call) -> render the returned SlideSpec as native shapes on a new slide.
 */

import { useCallback } from "react";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { readThemeColors } from "../../create-slide/hooks/themeColors";
import { EMU_PER_POINT, SLIDE_HEIGHT, SLIDE_WIDTH } from "../../../const";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { useSelectionWatcher } from "../../shared/hooks/useSelectionWatcher";
import { runAbortable, useAbortableRun } from "../../shared/hooks/useAbortableRun";
import {
  describeSketchToSlideFailure,
  getErrorMessage,
  logSketchToSlideError,
  postSketchToSlide,
  SKETCH_TO_SLIDE_REQUEST_TIMEOUT_MS,
} from "../services/sketchToSlideApi";
import { renderSlideSpec } from "../services/sketchToSlideRenderer";
import {
  captureSelectedSketchImage,
  NOT_AN_IMAGE_MESSAGE,
  readSelectedSketchShape,
} from "../services/sketchToSlideSelection";
import { uploadSketchImage } from "../services/sketchToSlideUpload";
import type { SketchToSlideState } from "../../../types";

const SKETCH_TO_SLIDE_FAILED_MESSAGE = "Converting the sketch failed";

// NOTE: hardcoded to the only Studio client that exists today — same
// placeholder usePhotoHeadshot.ts uses for its blob-sas upload. Once that
// call needs to support other tenants, replace both with the real client id
// (e.g. useOptionalAppConfig()?.data?.client.id) and drop the constant.
const SKETCH_TO_SLIDE_CLIENT_ID = 1;

const SELECTION_WATCHER_REASONS = {
  handlerAdd: "sketch-to-slide-selection-handler-add",
  handlerRemove: "sketch-to-slide-selection-handler-remove",
};

/** Read the presentation's slide size and convert to EMU for the wire request
 *  (DeckorateAPI's SlideDimensions is EMU; Office.js reports points). Falls
 *  back to the standard 16:9 default on any read failure. */
async function readSlideDimensionsEmu(): Promise<{ cx: number; cy: number }> {
  if (!hasPpt()) return { cx: SLIDE_WIDTH * EMU_PER_POINT, cy: SLIDE_HEIGHT * EMU_PER_POINT };
  try {
    return await ppt().run(async (context) => {
      const pageSetup = context.presentation.pageSetup;
      pageSetup.load("slideWidth,slideHeight");
      await context.sync();
      const widthPts = typeof pageSetup.slideWidth === "number" && pageSetup.slideWidth > 0 ? pageSetup.slideWidth : SLIDE_WIDTH;
      const heightPts = typeof pageSetup.slideHeight === "number" && pageSetup.slideHeight > 0 ? pageSetup.slideHeight : SLIDE_HEIGHT;
      return { cx: Math.round(widthPts * EMU_PER_POINT), cy: Math.round(heightPts * EMU_PER_POINT) };
    });
  } catch (error) {
    logSketchToSlideError("sketch-to-slide-dimensions-read", getErrorMessage(error, "pageSetup read failed"));
    return { cx: SLIDE_WIDTH * EMU_PER_POINT, cy: SLIDE_HEIGHT * EMU_PER_POINT };
  }
}

/** The 1-based index of the currently selected slide, for `deck_context.target_slide_index`
 *  (pass-through/logging only on the backend — never used to gate rendering). */
async function readActiveSlideIndex(): Promise<number | null> {
  if (!hasPpt()) return null;
  try {
    return await ppt().run(async (context) => {
      const slides = context.presentation.slides;
      const selected = context.presentation.getSelectedSlides();
      slides.load("items/id");
      selected.load("items/id");
      await context.sync();
      const activeId = selected.items[0]?.id;
      if (!activeId) return null;
      const index = slides.items.findIndex((s) => s.id === activeId);
      return index >= 0 ? index + 1 : null;
    });
  } catch (error) {
    logSketchToSlideError("sketch-to-slide-active-slide-index-read", getErrorMessage(error, "slide index read failed"));
    return null;
  }
}

export function useSketchToSlide() {
  const http = useDeckorateHttp();
  const { shape: selectedShape, selectionError } = useSelectionWatcher(
    readSelectedSketchShape,
    NOT_AN_IMAGE_MESSAGE,
    logSketchToSlideError,
    SELECTION_WATCHER_REASONS,
  );
  const { state, safeSetState, isRunningRef, abortRef, timedOutRef, cancel, reset } = useAbortableRun<SketchToSlideState>({
    status: "idle",
  });

  const run = useCallback(async () => {
    if (isRunningRef.current) return;
    if (!selectedShape) {
      safeSetState({ status: "error", message: NOT_AN_IMAGE_MESSAGE });
      return;
    }

    safeSetState({ status: "running", phase: "reading" });

    const outcome = await runAbortable({ isRunningRef, abortRef, timedOutRef }, SKETCH_TO_SLIDE_REQUEST_TIMEOUT_MS, async (signal) => {
      const source = await captureSelectedSketchImage(selectedShape);

      safeSetState({ status: "running", phase: "uploading" });
      const uploaded = await uploadSketchImage(http, SKETCH_TO_SLIDE_CLIENT_ID, source.base64, source.mimeType, { signal });

      safeSetState({ status: "running", phase: "analyzing" });
      const [themeColors, slideDimensions, targetSlideIndex] = await Promise.all([
        readThemeColors(),
        readSlideDimensionsEmu(),
        readActiveSlideIndex(),
      ]);
      const result = await postSketchToSlide(
        http,
        uploaded,
        {
          ...(themeColors ? { themeColors } : {}),
          slideDimensions,
          ...(targetSlideIndex !== null ? { targetSlideIndex } : {}),
        },
        { signal },
      );

      safeSetState({ status: "running", phase: "composing" });
      const rendered = await renderSlideSpec(result.slideSpec, themeColors);

      return {
        layoutFamily: result.layoutFamily,
        confidence: result.confidence,
        shapeCount: rendered.shapeCount,
        connectorCount: rendered.connectorCount,
      };
    });

    switch (outcome.kind) {
      case "skipped":
        return;
      case "cancelled":
        safeSetState({ status: "cancelled" });
        return;
      case "failed":
        logSketchToSlideError("sketch-to-slide-run", getErrorMessage(outcome.error, SKETCH_TO_SLIDE_FAILED_MESSAGE));
        safeSetState({
          status: "error",
          message: describeSketchToSlideFailure(outcome.error, SKETCH_TO_SLIDE_FAILED_MESSAGE, { timedOut: outcome.timedOut }),
        });
        return;
      case "ok":
        safeSetState({ status: "done", summary: outcome.value });
    }
  }, [http, selectedShape, safeSetState, isRunningRef, abortRef, timedOutRef]);

  return { state, selectedShape, selectionError, run, cancel, reset };
}
